import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import PlaceIcon from '@mui/icons-material/Place';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import { ColorField } from '../../../../components/ReelMaker/controls/ColorField';
import {
    CLOCK_FORMATS,
    CLOCK_FORMAT_LABELS,
    CRICKET_STYLES,
    CricketWidgetPayload,
    CricketWorkerConfig,
    COMPOSER_FONT_FAMILIES,
    ClockFormat,
    SERVER_REFRESH_OPTIONS,
    ScreenWidget,
    StockQuote,
    TICKER_REFRESH_OPTIONS,
    WIDGET_LABELS,
    WeatherNow,
    WidgetLook,
} from '../../../../components/ScreenComposer/composer.types';
import { ClockWidgetView, CricketScoreView, NewsTickerView, OnDemandPlayerView, StockTickerView, WeatherWidgetView } from '../../../../components/ScreenComposer/WidgetViews';
import { screenDialogBodySx } from '../dialogSx';
import { ScreenLocationDialog } from '../ScreenLocationDialog';
import { screensApi } from '../Screens.api';
import { NewsFeedPreview } from '../Screens.types';
import { newsItemsFrom } from './composerPreview';

export interface WidgetSettingsDialogProps {
    open: boolean;
    widget: ScreenWidget | null;
    screenId: string;
    screenLocation: string;
    screenTimezone: string;
    /** Public URL drawn as the idle QR code. */
    qrUrl?: string;
    designWidth?: number;
    designHeight?: number;
    onClose: () => void;
    onApply: (widget: ScreenWidget) => void;
}

/** Chromium lists some zones under their old names (Asia/Calcutta); offer the current names too. */
const CURRENT_ZONE_NAMES = ['Asia/Kolkata', 'Asia/Kathmandu', 'Asia/Ho_Chi_Minh', 'Asia/Yangon', 'Europe/Kyiv', 'America/Nuuk', 'Pacific/Kanton'];

const timeZones = (): string[] => {
    let zones: string[];
    try {
        zones = (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf('timeZone');
    } catch {
        zones = ['UTC', 'Europe/London', 'Europe/Paris', 'America/New_York', 'Australia/Sydney'];
    }
    return [...new Set([...zones, ...CURRENT_ZONE_NAMES, 'UTC'])].sort();
};

const previewBoxSx = {
    height: 120,
    borderRadius: 1,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'divider',
    background: 'repeating-conic-gradient(rgba(0,0,0,0.25) 0% 25%, rgba(255,255,255,0.12) 0% 50%) 50% / 16px 16px',
} as const;

/** Font, size and colours shared by the weather, ticker and time widgets. */
const LookFields: React.FC<{ look: WidgetLook; onChange: (patch: Partial<WidgetLook>) => void }> = ({ look, onChange }) => (
    <Stack spacing={2}>
        <FormControl size="small" fullWidth>
            <InputLabel id="widget-font">Font</InputLabel>
            <Select labelId="widget-font" label="Font" value={look.fontFamily} onChange={(e) => onChange({ fontFamily: e.target.value })}>
                {COMPOSER_FONT_FAMILIES.map((f) => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontFamily: f.value }}>
                        {f.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
        <Box>
            <Typography variant="caption" color="text.secondary">
                Font size {look.fontSize}px (on the screen)
            </Typography>
            <Slider size="small" color="secondary" min={8} max={240} value={look.fontSize} onChange={(_e, v) => onChange({ fontSize: v as number })} aria-label="Font size" />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <ColorField label="Font colour" value={look.color} onChange={(color) => onChange({ color })} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <ColorField label="Background colour" value={look.backgroundColor} onChange={(backgroundColor) => onChange({ backgroundColor })} allowTransparent />
            </Box>
        </Stack>
    </Stack>
);

/** Settings for one Composer widget. Changes apply when the user presses Apply. */
export const WidgetSettingsDialog: React.FC<WidgetSettingsDialogProps> = ({ open, widget, screenId, screenLocation, screenTimezone, qrUrl, designWidth = 1920, designHeight = 1080, onClose, onApply }) => {
    const [draft, setDraft] = useState<ScreenWidget | null>(widget);
    const [mapOpen, setMapOpen] = useState(false);
    const [weather, setWeather] = useState<WeatherNow | null>(null);
    const [weatherNote, setWeatherNote] = useState('');
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [feed, setFeed] = useState<NewsFeedPreview | null>(null);
    const [feedError, setFeedError] = useState('');
    const [feedLoading, setFeedLoading] = useState(false);
    const [symbolQuery, setSymbolQuery] = useState('');
    const [symbolHits, setSymbolHits] = useState<Array<{ symbol: string; name: string; exchange: string }>>([]);
    const [stockQuotes, setStockQuotes] = useState<StockQuote[]>([]);
    const [cricketPayload, setCricketPayload] = useState<CricketWidgetPayload | null>(null);
    const [cricketWorker, setCricketWorker] = useState<CricketWorkerConfig | null>(null);
    const zones = useMemo(timeZones, []);

    useEffect(() => {
        if (!open) return;
        setDraft(widget);
        setFeed(null);
        setFeedError('');
        setWeather(null);
        setWeatherNote('');
        // Reset only when a different widget is opened, not when the parent re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, widget?.id]);

    const patchConfig = (patch: Record<string, unknown>) =>
        setDraft((d) => (d ? ({ ...d, config: { ...d.config, ...patch } } as ScreenWidget) : d));

    const weatherLocation = draft?.type === 'weather' ? draft.config.locationOverride : '';
    const weatherLabel = draft?.type === 'weather' ? draft.config.labelOverride : '';
    useEffect(() => {
        if (!open || draft?.type !== 'weather') return undefined;
        let cancelled = false;
        const t = setTimeout(async () => {
            setWeatherLoading(true);
            try {
                const res = await screensApi.weatherPreview(screenId, weatherLocation, weatherLabel);
                if (cancelled) return;
                if (res.available && typeof res.tempC === 'number') {
                    setWeather({ location: res.location || '', tempC: res.tempC, icon: res.icon || '', label: res.label || '' });
                    setWeatherNote('');
                } else {
                    setWeather(null);
                    setWeatherNote(res.reason || 'Weather is not available');
                }
            } catch (error) {
                if (!cancelled) setWeatherNote(error instanceof Error ? error.message : 'Could not load the weather');
            } finally {
                if (!cancelled) setWeatherLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [open, draft?.type, weatherLocation, weatherLabel, screenId]);

    const loadFeed = async (url: string, refresh = false) => {
        if (!url.trim()) return;
        setFeedLoading(true);
        setFeedError('');
        try {
            const result = await screensApi.newsPreview(url.trim(), refresh);
            setFeed(result);
            // Keep the chosen fields that exist in this feed; fall back to the title.
            setDraft((d) => {
                if (d?.type !== 'news-ticker' || d.config.fields.some((f) => result.fields.includes(f)) || !result.fields.length) return d;
                return { ...d, config: { ...d.config, fields: [result.fields.includes('title') ? 'title' : result.fields[0]] } };
            });
        } catch (error) {
            setFeed(null);
            setFeedError(error instanceof Error ? error.message : 'Could not load the feed');
        } finally {
            setFeedLoading(false);
        }
    };
    const draftFeedUrl = draft?.type === 'news-ticker' ? draft.config.feedUrl : '';
    const stockSymbols = draft?.type === 'stock-ticker' ? draft.config.symbols.join(',') : '';
    const stockServerRefresh = draft?.type === 'stock-ticker' ? draft.config.serverRefreshSec : 30;
    const stockTickerRefresh = draft?.type === 'stock-ticker' ? draft.config.tickerRefreshSec : 30;

    useEffect(() => {
        if (!open || !stockSymbols) return undefined;
        let cancelled = false;
        const load = () => {
            screensApi
                .stockQuotes(stockSymbols.split(','), stockServerRefresh)
                .then((res) => {
                    if (!cancelled) setStockQuotes(res.quotes);
                })
                .catch(() => undefined);
        };
        load();
        const timer = setInterval(load, Math.max(15, stockTickerRefresh) * 1000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [open, stockSymbols, stockServerRefresh, stockTickerRefresh]);

    useEffect(() => {
        const query = symbolQuery.trim();
        if (query.length < 1) {
            setSymbolHits([]);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            screensApi
                .stockSearch(query)
                .then((hits) => {
                    if (!cancelled) setSymbolHits(hits);
                })
                .catch(() => undefined);
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [symbolQuery]);

    const cricketTeams = draft?.type === 'cricket-score' ? draft.config.teams.join(',') : '';
    const cricketTypes = draft?.type === 'cricket-score' ? draft.config.matchTypes.join(',') : '';
    const cricketRefresh = draft?.type === 'cricket-score' ? draft.config.displayRefreshSec : 10;
    useEffect(() => {
        if (!open || draft?.type !== 'cricket-score') return undefined;
        let cancelled = false;
        const load = () => {
            screensApi.cricketScore(cricketTeams.split(',').filter(Boolean), cricketTypes.split(',').filter(Boolean)).then((res) => { if (!cancelled) setCricketPayload(res); }).catch(() => undefined);
            screensApi.cricketTeams().then((res) => { if (!cancelled) setCricketWorker(res.config); }).catch(() => undefined);
        };
        load();
        const timer = setInterval(load, Math.max(5, cricketRefresh) * 1000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [open, draft?.type, cricketTeams, cricketTypes, cricketRefresh]);

    useEffect(() => {
        if (open && widget?.type === 'news-ticker' && widget.config.feedUrl) void loadFeed(widget.config.feedUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, widget?.id]);

    if (!draft) return null;
    const sampleItems = draft.type === 'news-ticker' && feed ? newsItemsFrom(feed.items, draft.config.fields, draft.config.fieldJoiner, draft.config.maxItems, screenTimezone) : [];

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    {WIDGET_LABELS[draft.type]} settings · {draft.name}
                </DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <Stack spacing={2}>
                        <TextField
                            label="Name"
                            size="small"
                            value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value.slice(0, 60) } as ScreenWidget)}
                            helperText="Shown in the layers list only."
                        />
                        {draft.type !== 'news-ticker' && draft.type !== 'stock-ticker' && (
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    label="Width (px)"
                                    size="small"
                                    type="number"
                                    value={Math.round(draft.width * designWidth)}
                                    onChange={(e) => {
                                        const px = Math.max(1, Number(e.target.value) || 1);
                                        setDraft({ ...draft, width: Math.min(1, px / designWidth) });
                                    }}
                                    inputProps={{ min: 1, step: 1 }}
                                />
                                <TextField
                                    label="Height (px)"
                                    size="small"
                                    type="number"
                                    value={Math.round(draft.height * designHeight)}
                                    onChange={(e) => {
                                        const px = Math.max(1, Number(e.target.value) || 1);
                                        setDraft({ ...draft, height: Math.min(1, px / designHeight) });
                                    }}
                                    inputProps={{ min: 1, step: 1 }}
                                />
                            </Stack>
                        )}

                        {draft.type === 'reel-player' && (
                            <>
                                <Alert severity="info">The screen's playlist plays inside this box. A screen can have one Reel Player.</Alert>
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="reel-fit">Fit</InputLabel>
                                    <Select labelId="reel-fit" label="Fit" value={draft.config.fit} onChange={(e) => patchConfig({ fit: e.target.value })}>
                                        <MenuItem value="contain">Fit inside the box (letterbox)</MenuItem>
                                        <MenuItem value="cover">Fill the box (crop the edges)</MenuItem>
                                    </Select>
                                </FormControl>
                                <ColorField label="Letterbox colour" value={draft.config.backgroundColor} onChange={(backgroundColor) => patchConfig({ backgroundColor })} />
                            </>
                        )}

                        {draft.type === 'weather' && (
                            <>
                                <Box sx={previewBoxSx}>
                                    <WeatherWidgetView config={{ ...draft.config, fontSize: Math.min(draft.config.fontSize, 48) }} weather={weather} />
                                </Box>
                                {weatherLoading && <Typography variant="caption" color="text.secondary">Loading the current weather…</Typography>}
                                {!weatherLoading && weatherNote && <Alert severity="warning">{weatherNote}</Alert>}
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <TextField
                                        label="Location override"
                                        size="small"
                                        fullWidth
                                        value={draft.config.locationOverride}
                                        placeholder={screenLocation ? `Screen location: ${screenLocation}` : 'This screen has no location'}
                                        InputProps={{ readOnly: true }}
                                        onClick={() => setMapOpen(true)}
                                        helperText="Leave empty to show the weather at the screen's own location."
                                    />
                                    <Button variant="outlined" color="secondary" startIcon={<PlaceIcon />} onClick={() => setMapOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                                        Choose
                                    </Button>
                                    {draft.config.locationOverride && (
                                        <Button color="secondary" onClick={() => patchConfig({ locationOverride: '' })}>
                                            Clear
                                        </Button>
                                    )}
                                </Stack>
                                <TextField
                                    label="Location label"
                                    size="small"
                                    value={draft.config.labelOverride}
                                    onChange={(e) => patchConfig({ labelOverride: e.target.value.slice(0, 60) })}
                                    placeholder={weather?.location || 'Taken from the location'}
                                    helperText="Optional. Replaces the place name shown under the temperature."
                                />
                                <FormControlLabel
                                    control={<Switch color="secondary" checked={draft.config.showIcon} onChange={(e) => patchConfig({ showIcon: e.target.checked })} />}
                                    label="Show weather icon"
                                />
                                <LookFields look={draft.config} onChange={patchConfig} />
                            </>
                        )}

                        {draft.type === 'news-ticker' && (
                            <>
                                <Box sx={{ ...previewBoxSx, height: 72 }}>
                                    <NewsTickerView config={{ ...draft.config, fontSize: Math.min(draft.config.fontSize, 36) }} items={sampleItems} placeholder="Load the feed to preview the ticker" />
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <TextField
                                        label="RSS feed URL"
                                        size="small"
                                        fullWidth
                                        value={draft.config.feedUrl}
                                        onChange={(e) => patchConfig({ feedUrl: e.target.value.slice(0, 2000) })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') void loadFeed(draftFeedUrl, true);
                                        }}
                                        placeholder="https://feeds.bbci.co.uk/news/rss.xml"
                                    />
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={feedLoading ? <CircularProgress size={14} color="inherit" /> : <RssFeedIcon />}
                                        disabled={feedLoading || !draft.config.feedUrl.trim()}
                                        onClick={() => void loadFeed(draftFeedUrl, true)}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Load feed
                                    </Button>
                                </Stack>
                                {feedError && <Alert severity="error">{feedError}</Alert>}
                                {feed && (
                                    <Box>
                                        <Typography variant="subtitle2">{feed.title || 'Feed'} · fields to show</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Ticked fields of each news item are joined with the field joiner below.
                                        </Typography>
                                        <FormGroup>
                                            {feed.fields.map((field) => (
                                                <FormControlLabel
                                                    key={field}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="secondary"
                                                            checked={draft.config.fields.includes(field)}
                                                            onChange={(e) => {
                                                                const fields = e.target.checked
                                                                    ? [...draft.config.fields.filter((f) => f !== field), field]
                                                                    : draft.config.fields.filter((f) => f !== field);
                                                                patchConfig({ fields: fields.slice(-6) });
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                                                                {`<${field}>`}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                                                {(feed.items.find((i) => i[field])?.[field] || '').slice(0, 70)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                )}
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        label="Field joiner"
                                        size="small"
                                        value={draft.config.fieldJoiner}
                                        onChange={(e) => patchConfig({ fieldJoiner: e.target.value.slice(0, 10) })}
                                        helperText="Between the fields of one item"
                                    />
                                    <TextField
                                        label="Separator"
                                        size="small"
                                        value={draft.config.separator}
                                        onChange={(e) => patchConfig({ separator: e.target.value.slice(0, 20) })}
                                        helperText="Between news items"
                                    />
                                    <TextField
                                        label="Items"
                                        size="small"
                                        type="number"
                                        value={draft.config.maxItems}
                                        onChange={(e) => patchConfig({ maxItems: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })}
                                        inputProps={{ min: 1, max: 50 }}
                                        helperText="Newest first"
                                        sx={{ width: 110 }}
                                    />
                                </Stack>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Speed {draft.config.speedPxPerSec} px per second
                                    </Typography>
                                    <Slider
                                        size="small"
                                        color="secondary"
                                        min={20}
                                        max={600}
                                        step={10}
                                        value={draft.config.speedPxPerSec}
                                        onChange={(_e, v) => patchConfig({ speedPxPerSec: v as number })}
                                        aria-label="Ticker speed"
                                    />
                                </Box>
                                <LookFields look={draft.config} onChange={patchConfig} />
                            </>
                        )}

                        {draft.type === 'cricket-score' && (
                            <>
                                <Box sx={{ ...previewBoxSx, height: 96 }}>
                                    <CricketScoreView config={{ ...draft.config, fontSize: Math.min(draft.config.fontSize, 22) }} payload={cricketPayload} placeholder timezone={draft.config.timezoneOverride || screenTimezone} />
                                </Box>
                                <Alert severity={cricketWorker ? 'info' : 'warning'}>
                                    {cricketWorker
                                        ? `Worker checks every ${cricketWorker.discoverySec}s, live score every ${cricketWorker.liveSec}s, detail every ${cricketWorker.detailSec}s. Scores expire after ${cricketWorker.ttlSec}s. API usage ${cricketPayload?.usage ? `${cricketPayload.usage.hitsToday}/${cricketPayload.usage.hitsLimit}` : 'unknown'}.`
                                        : 'Cricket worker offline. Frequencies are set in the worker environment, not here.'}
                                </Alert>
                                <Autocomplete
                                    multiple
                                    options={cricketWorker?.teams || draft.config.teams}
                                    value={draft.config.teams}
                                    onChange={(_e, value) => patchConfig({ teams: value.slice(0, 10) })}
                                    renderInput={(params) => <TextField {...params} label="Whitelisted teams" size="small" />}
                                />
                                <FormControl size="small">
                                    <InputLabel>Style</InputLabel>
                                    <Select label="Style" value={draft.config.style} onChange={(e) => patchConfig({ style: e.target.value as typeof draft.config.style })}>
                                        {CRICKET_STYLES.map((style) => <MenuItem key={style} value={style}>{style}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControlLabel control={<Switch checked={draft.config.showNextMatch} onChange={(_e, v) => patchConfig({ showNextMatch: v })} />} label="Show the next scheduled match when nothing is live" />
                                <Autocomplete
                                    size="small"
                                    options={zones}
                                    value={draft.config.timezoneOverride || null}
                                    onChange={(_e, v) => patchConfig({ timezoneOverride: v || '' })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Next match timezone"
                                            placeholder={`Screen timezone (${screenTimezone || 'UTC'})`}
                                            helperText="The next match is shown as dd/mm/yyyy hh:mm. Leave empty to use the screen timezone."
                                        />
                                    )}
                                />
                                <TextField label="Rotate every (seconds)" type="number" size="small" value={draft.config.rotateSec} onChange={(e) => patchConfig({ rotateSec: Number(e.target.value) || 15 })} />
                                <FormControl size="small">
                                    <InputLabel>Screen refresh</InputLabel>
                                    <Select label="Screen refresh" value={draft.config.displayRefreshSec} onChange={(e) => patchConfig({ displayRefreshSec: Number(e.target.value) })}>
                                        {[5, 10, 15, 30].map((sec) => <MenuItem key={sec} value={sec}>Every {sec} seconds</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <LookFields look={draft.config} onChange={patchConfig} />
                            </>
                        )}

                        {draft.type === 'stock-ticker' && (
                            <>
                                <Box sx={{ ...previewBoxSx, height: 72 }}>
                                    <StockTickerView
                                        config={{ ...draft.config, fontSize: Math.min(draft.config.fontSize, 32) }}
                                        quotes={stockQuotes}
                                        placeholder="Prices appear once the symbols are known"
                                    />
                                </Box>
                                <Alert severity="info">Prices may be delayed by up to 15 minutes. Yahoo Finance has no official data feed.</Alert>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {draft.config.symbols.map((symbol) => (
                                        <Chip
                                            key={symbol}
                                            label={symbol}
                                            size="small"
                                            onDelete={() => patchConfig({ symbols: draft.config.symbols.filter((s) => s !== symbol) })}
                                        />
                                    ))}
                                </Box>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={symbolHits}
                                    inputValue={symbolQuery}
                                    onInputChange={(_e, value) => setSymbolQuery(value)}
                                    getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.symbol} — ${option.name}${option.exchange ? ` (${option.exchange})` : ''}`)}
                                    onChange={(_e, value) => {
                                        const symbol = (typeof value === 'string' ? value : value?.symbol || '').trim().toUpperCase();
                                        if (!symbol || draft.config.symbols.includes(symbol) || draft.config.symbols.length >= 30) return;
                                        if (!/^[A-Z0-9.^=-]{1,20}$/.test(symbol)) return;
                                        patchConfig({ symbols: [...draft.config.symbols, symbol] });
                                        setSymbolQuery('');
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Add a symbol" placeholder="Search Apple, or type AAPL" />}
                                />
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="stock-label">Show</InputLabel>
                                    <Select labelId="stock-label" label="Show" value={draft.config.labelField} onChange={(e) => patchConfig({ labelField: e.target.value })}>
                                        <MenuItem value="both">Name and symbol</MenuItem>
                                        <MenuItem value="name">Name only</MenuItem>
                                        <MenuItem value="symbol">Symbol only</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormGroup>
                                    <FormControlLabel control={<Switch color="secondary" checked={draft.config.showTrend} onChange={(e) => patchConfig({ showTrend: e.target.checked })} />} label="Trend arrow" />
                                    <FormControlLabel control={<Switch color="secondary" checked={draft.config.showChange} onChange={(e) => patchConfig({ showChange: e.target.checked })} />} label="Price change" />
                                    <FormControlLabel control={<Switch color="secondary" checked={draft.config.showChangePercent} onChange={(e) => patchConfig({ showChangePercent: e.target.checked })} />} label="Change percent" />
                                    <FormControlLabel control={<Switch color="secondary" checked={draft.config.showCurrency} onChange={(e) => patchConfig({ showCurrency: e.target.checked })} />} label="Currency" />
                                </FormGroup>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <ColorField label="Up colour" value={draft.config.upColor} onChange={(upColor) => patchConfig({ upColor })} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <ColorField label="Down colour" value={draft.config.downColor} onChange={(downColor) => patchConfig({ downColor })} />
                                    </Box>
                                </Stack>
                                <TextField
                                    label="Separator"
                                    size="small"
                                    value={draft.config.separator}
                                    onChange={(e) => patchConfig({ separator: e.target.value.slice(0, 20) })}
                                    helperText="Between quotes"
                                />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Speed {draft.config.speedPxPerSec} px per second
                                    </Typography>
                                    <Slider size="small" color="secondary" min={20} max={600} step={10} value={draft.config.speedPxPerSec} onChange={(_e, v) => patchConfig({ speedPxPerSec: v as number })} aria-label="Ticker speed" />
                                </Box>
                                <Typography variant="subtitle2">Refresh rate</Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="server-refresh">Server refresh</InputLabel>
                                        <Select
                                            labelId="server-refresh"
                                            label="Server refresh"
                                            value={draft.config.serverRefreshSec}
                                            onChange={(e) => patchConfig({ serverRefreshSec: Number(e.target.value) })}
                                        >
                                            {SERVER_REFRESH_OPTIONS.map((sec) => (
                                                <MenuItem key={sec} value={sec}>
                                                    Every {sec < 60 ? `${sec} seconds` : `${sec / 60} minute${sec === 60 ? '' : 's'}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="ticker-refresh">Ticker refresh</InputLabel>
                                        <Select
                                            labelId="ticker-refresh"
                                            label="Ticker refresh"
                                            value={draft.config.tickerRefreshSec}
                                            onChange={(e) => patchConfig({ tickerRefreshSec: Number(e.target.value) })}
                                        >
                                            {TICKER_REFRESH_OPTIONS.map((sec) => (
                                                <MenuItem key={sec} value={sec}>
                                                    Every {sec < 60 ? `${sec} seconds` : `${sec / 60} minute${sec === 60 ? '' : 's'}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Server refresh is how often new prices are fetched from Yahoo, shared by every screen. Ticker refresh is how often this ticker asks for them.
                                    {draft.config.tickerRefreshSec < draft.config.serverRefreshSec ? ' This ticker asks more often than the server fetches, so it will repeat the cached prices.' : ''}
                                </Typography>
                                <LookFields look={draft.config} onChange={patchConfig} />
                            </>
                        )}

                        {draft.type === 'clock' && (
                            <>
                                <Box sx={previewBoxSx}>
                                    <ClockWidgetView config={{ ...draft.config, fontSize: Math.min(draft.config.fontSize, 48) }} timezone={screenTimezone} />
                                </Box>
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="clock-format">Date and time format</InputLabel>
                                    <Select
                                        labelId="clock-format"
                                        label="Date and time format"
                                        value={draft.config.format}
                                        onChange={(e) => patchConfig({ format: e.target.value as ClockFormat })}
                                    >
                                        {CLOCK_FORMATS.map((f) => (
                                            <MenuItem key={f} value={f}>
                                                {CLOCK_FORMAT_LABELS[f]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControlLabel
                                    control={<Switch color="secondary" checked={Boolean(draft.config.showDate)} onChange={(_e, v) => patchConfig({ showDate: v })} />}
                                    label="Show date"
                                />
                                <Autocomplete
                                    size="small"
                                    options={zones}
                                    value={draft.config.timezoneOverride || null}
                                    onChange={(_e, v) => patchConfig({ timezoneOverride: v || '' })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Timezone override"
                                            placeholder={`Screen timezone (${screenTimezone || 'UTC'})`}
                                            helperText={`Leave empty to use the screen's timezone, ${screenTimezone || 'UTC'}, which follows its location.`}
                                        />
                                    )}
                                />
                                <LookFields look={draft.config} onChange={patchConfig} />
                            </>
                        )}
                        {draft.type === 'on-demand-player' && (
                            <>
                                <Alert severity="info">With nothing queued, this box shows the QR code and title. Drag and resize it on the screen. While a playlist plays, the same box is the player. Its place in the layers list is its position on screen.</Alert>
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="odp-idle">When the queue is empty</InputLabel>
                                    <Select
                                        labelId="odp-idle"
                                        label="When the queue is empty"
                                        value={draft.config.idleDisplay}
                                        onChange={(e) => patchConfig({ idleDisplay: e.target.value === 'none' ? 'none' : 'qr' })}
                                    >
                                        <MenuItem value="qr">QR code and title</MenuItem>
                                        <MenuItem value="none">Nothing</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField size="small" label="QR code URL" value={draft.config.qrUrl || ''} placeholder={qrUrl || 'https://'} onChange={(e) => patchConfig({ qrUrl: e.target.value.slice(0, 500) })} helperText="Leave empty to use this screen's On Demand link." fullWidth />
                                <TextField size="small" label="Title under the QR code" value={draft.config.caption} onChange={(e) => patchConfig({ caption: e.target.value.slice(0, 80) })} fullWidth />
                                <TextField size="small" label="Greeting before a playlist" value={draft.config.greeting || 'Now playing'} onChange={(e) => patchConfig({ greeting: e.target.value.slice(0, 80) })} fullWidth />
                                <TextField size="small" label="Thank you after a playlist" value={draft.config.thankYou || 'Thank you'} onChange={(e) => patchConfig({ thankYou: e.target.value.slice(0, 80) })} fullWidth />
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="odp-fit">Video fit</InputLabel>
                                    <Select labelId="odp-fit" label="Video fit" value={draft.config.fit} onChange={(e) => patchConfig({ fit: e.target.value === 'cover' ? 'cover' : 'contain' })}>
                                        <MenuItem value="contain">Contain</MenuItem>
                                        <MenuItem value="cover">Cover</MenuItem>
                                    </Select>
                                </FormControl>
                                <ColorField label="Player background" value={draft.config.backgroundColor} onChange={(backgroundColor) => patchConfig({ backgroundColor })} />
                                <ColorField label="Message colour" value={draft.config.messageColor || '#ffffff'} onChange={(messageColor) => patchConfig({ messageColor })} />
                                <TextField size="small" label="Message size (px)" type="number" value={draft.config.messageFontSize || 28} onChange={(e) => patchConfig({ messageFontSize: Math.min(160, Math.max(8, Number(e.target.value) || 28)) })} inputProps={{ min: 8, max: 160 }} />
                                <ColorField label="Message background" value={draft.config.messageBackground || 'rgba(0,0,0,0.72)'} onChange={(messageBackground) => patchConfig({ messageBackground })} />
                                <Box sx={{ height: 220, borderRadius: 1, overflow: 'hidden', background: '#111', position: 'relative' }}>
                                    <OnDemandPlayerView config={draft.config} qrUrl={qrUrl} />
                                </Box>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => onApply(draft)}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
            {draft.type === 'weather' && (
                <ScreenLocationDialog
                    open={mapOpen}
                    value={draft.config.locationOverride || screenLocation}
                    onClose={() => setMapOpen(false)}
                    onSelect={(next) => {
                        patchConfig({ locationOverride: next });
                        setMapOpen(false);
                    }}
                />
            )}
        </>
    );
};

export default WidgetSettingsDialog;
